"use client";

import React, { useState, useCallback } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import ThreatToastContainer from './ThreatToastContainer';
import PageTransition from './PageTransition';
import { LanguageProvider } from '@/lib/language-context';
import { ThreatToastProvider, useThreatToast } from '@/contexts/ThreatToastContext';
import { useThreatFeed, ThreatFeedItem } from '@/lib/supabase/realtime-hooks';
import { useAppToast } from '@/lib/use-app-toast';

interface AppLayoutProps {
  children: React.ReactNode;
  currentPath: string;
}

// Inner component that can access ThreatToastProvider context
function AppLayoutInner({ children, currentPath }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { addToast, unreadCount, clearUnread } = useThreatToast();
  const { alert } = useAppToast();

  const handleNewHighSeverity = useCallback(
    (item: ThreatFeedItem) => {
      addToast({
        severity: item.severity as 'critical' | 'high',
        threat_type: item.threat_type,
        target: item.target,
        source_ip: item.source_ip,
        technique: item.technique,
        occurred_at: item.occurred_at,
      });
      // Also fire an alert toast at top-right for immediate visibility
      if (item.severity === 'critical') {
        alert(
          `⚠ CRITICAL: ${item.threat_type} Detected`,
          `Target: ${item.target} · ${item.source_ip}`
        );
      }
    },
    [addToast, alert]
  );

  // Subscribe to live feed at layout level to catch all new high-severity threats
  useThreatFeed(handleNewHighSeverity);

  return (
    <div className="flex h-screen bg-cyber-black overflow-hidden grid-line-bg">
      {/* Mobile Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentPath={currentPath}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      {/* Main content */}
      <div
        className="flex flex-col flex-1 overflow-hidden transition-all duration-300 ease-in-out"
        style={{ marginLeft: 0 }}
      >
        <Topbar
          sidebarCollapsed={sidebarCollapsed}
          onMobileMenuOpen={() => setMobileSidebarOpen(true)}
          alertBadgeCount={unreadCount}
          onAlertBadgeClear={clearUnread}
        />

        <main className="flex-1 overflow-y-auto scrollbar-cyber">
          <div className="p-4 md:p-6 xl:p-8 max-w-screen-2xl mx-auto">
            <PageTransition>
              {children}
            </PageTransition>
          </div>
        </main>
      </div>

      {/* Global toast container */}
      <ThreatToastContainer />
    </div>
  );
}

export default function AppLayout({ children, currentPath }: AppLayoutProps) {
  return (
    <LanguageProvider>
      <ThreatToastProvider>
        <AppLayoutInner currentPath={currentPath}>
          {children}
        </AppLayoutInner>
      </ThreatToastProvider>
    </LanguageProvider>
  );
}